// Espelho ao vivo da aba do sistema (Ctrl+@) — lado do AGENTE.
//
// Um supervisor (permissão agents.screen_view) pede pelo hub; esta aba recebe "EspelhoIniciar", grava a
// própria página com o rrweb (só o DOM do WootDash — nunca outras janelas, nada de vídeo; senha sai
// mascarada) e manda lotes de eventos, compactados (gzip+base64) e em pedaços abaixo do limite de 32 KB do
// SignalR, pro grupo da sessão. Regras:
// - só a aba VISÍVEL grava: escondeu a aba/minimizou → para e avisa "oculta"; voltou → foto inteira de novo;
// - sem "EspelhoManter" do supervisor por 30 s (fechou a aba, caiu a rede) → para sozinha;
// - o agente vê um aviso de que a tela dele está sendo vista (transparência — LGPD).
import { ref } from 'vue'
import { record, type eventWithTime } from 'rrweb'
import { invokeHub, onHubEvent } from '@/composables/useConversationRealtime'
import { MarkBoard, type MarkMessage } from '@/utils/mirrorMarks'

const FLUSH_MS = 400
const KEEPALIVE_TIMEOUT_MS = 30_000
export const MIRROR_CHUNK_SIZE = 28_000

interface MirrorSession { viewerName: string; lastSeen: number }

/** Nomes de quem está vendo esta tela agora (pro aviso ao agente). */
export const mirrorWatchers = ref<string[]>([])

// ── Marcações do supervisor (lápis, seta, texto, ponteiro) — ScreenMirrorMarks.vue desenha por cima da tela ──
/** Estado das marcações nesta aba. marksVersion muda a cada mensagem (o canvas redesenha). */
export const mirrorBoard = new MarkBoard()
export const marksVersion = ref(0)
/** O agente escondeu as marcações (botão no aviso do espelho). */
export const mirrorMarksHidden = ref(false)

/** Limpa as marcações (trocou de página, redimensionou) e avisa quem está vendo, pra limpar lá também. */
export function clearMirrorMarks(): void {
  if (mirrorBoard.isEmpty) return
  mirrorBoard.apply({ op: 'clear' })
  marksVersion.value++
  sendState('marcacoes_limpas')
}

export function setMirrorMarksHidden(hidden: boolean): void {
  mirrorMarksHidden.value = hidden
  sendState(hidden ? 'marcacoes_ocultas' : 'marcacoes_visiveis')
}

/** Popup do supervisor (Ctrl+@). */
export const screenMirrorOpen = ref(false)

const sessions = new Map<string, MirrorSession>()
const tabId = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Date.now() + Math.random())
let stopRecording: (() => void) | undefined
let buffer: eventWithTime[] = []
let seq = 0
let flushing = false
let flushTimer: ReturnType<typeof setInterval> | undefined
let watchdog: ReturnType<typeof setInterval> | undefined
let started = false
let unsubscribers: (() => void)[] = []
const onPageHide = () => sendState('encerrado')

// ── codec: JSON → gzip → base64 (e volta) ────────────────────────────────────────────────────

async function streamToBytes(stream: ReadableStream<Uint8Array>): Promise<Uint8Array> {
  return new Uint8Array(await new Response(stream).arrayBuffer())
}

export async function packMirrorEvents(events: unknown[]): Promise<string> {
  const json = new Response(new TextEncoder().encode(JSON.stringify(events))).body!.pipeThrough(new CompressionStream('gzip'))
  const bytes = await streamToBytes(json)
  let binary = ''
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  return btoa(binary)
}

export async function unpackMirrorEvents(base64: string): Promise<eventWithTime[]> {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  const text = await new Response(new Response(bytes).body!.pipeThrough(new DecompressionStream('gzip'))).text()
  return JSON.parse(text)
}

export function splitMirrorChunks(payload: string, size = MIRROR_CHUNK_SIZE): string[] {
  const chunks: string[] = []
  for (let i = 0; i < payload.length; i += size) chunks.push(payload.slice(i, i + size))
  return chunks.length ? chunks : ['']
}

// ── gravação ─────────────────────────────────────────────────────────────────────────────────

function refreshWatchers() {
  mirrorWatchers.value = [...new Set([...sessions.values()].map(s => s.viewerName))]
}

function sendState(estado: 'visivel' | 'oculta' | 'encerrado' | 'marcacoes_limpas' | 'marcacoes_ocultas' | 'marcacoes_visiveis') {
  sessions.forEach((_, sessionId) => { invokeHub('EstadoEspelho', sessionId, tabId, estado).catch(() => { /* best-effort */ }) })
}

async function flush() {
  if (flushing || !buffer.length || !sessions.size) return
  flushing = true
  const events = buffer
  buffer = []
  try {
    const chunks = splitMirrorChunks(await packMirrorEvents(events))
    const batch = ++seq
    for (const sessionId of sessions.keys()) {
      for (let i = 0; i < chunks.length; i++) {
        await invokeHub('EnviarQuadrosEspelho', sessionId, tabId, batch, i, chunks.length, chunks[i])
      }
    }
  } catch {
    // Sem conexão: o lote se perde; ao reconectar o supervisor pede uma foto inteira de novo (ManterEspelho).
  } finally {
    flushing = false
  }
}

function startRecording() {
  if (stopRecording || !sessions.size || document.visibilityState !== 'visible') return
  buffer = []
  stopRecording = record({
    emit: e => { buffer.push(e) },
    // Senha já sai mascarada por padrão; o resto do que o agente vê vai como está (é a tela do sistema).
    maskInputOptions: { password: true },
    sampling: { mousemove: 80, scroll: 150, input: 'last' },
    recordCanvas: false,
    inlineStylesheet: true,
    blockClass: 'mirror-block',
  }) ?? undefined
  flushTimer = setInterval(() => { void flush() }, FLUSH_MS)
  sendState('visivel')
}

function stopRecordingNow() {
  stopRecording?.()
  stopRecording = undefined
  if (flushTimer) clearInterval(flushTimer)
  flushTimer = undefined
}

function endSession(sessionId: string) {
  sessions.delete(sessionId)
  refreshWatchers()
  if (!sessions.size) {
    stopRecordingNow()
    buffer = []
    // Ninguém mais vendo: as marcações somem e o "esconder" volta ao normal pra próxima vez.
    mirrorBoard.apply({ op: 'clear' })
    marksVersion.value++
    mirrorMarksHidden.value = false
  }
}

function onVisibilityChange() {
  if (!sessions.size) return
  if (document.visibilityState === 'visible') {
    startRecording()
  } else {
    void flush().finally(() => {
      stopRecordingNow()
      sendState('oculta')
    })
  }
}

/** Liga a escuta do espelho nesta aba (idempotente). Chamado no App.vue junto com o socket. */
export function startScreenMirrorAgent(): void {
  if (started) return
  started = true

  unsubscribers = [
    onHubEvent('EspelhoIniciar', (evt: { sessionId: string; viewerName: string }) => {
      sessions.set(evt.sessionId, { viewerName: evt.viewerName, lastSeen: Date.now() })
      refreshWatchers()
      if (stopRecording) record.takeFullSnapshot(true)
      else startRecording()
      if (document.visibilityState !== 'visible') sendState('oculta')
    }),

    onHubEvent('EspelhoParar', (evt: { sessionId: string }) => endSession(evt.sessionId)),

    // Marcação do supervisor: só de uma sessão que esta aba conhece (o id da sessão é segredo entre os dois).
    onHubEvent('MarcacaoEspelho', (evt: { sessionId: string; mark: string }) => {
      if (!sessions.has(evt.sessionId)) return
      let msg: MarkMessage
      try { msg = JSON.parse(evt.mark) } catch { return }
      if (mirrorBoard.apply(msg)) marksVersion.value++
    }),

    onHubEvent('EspelhoManter', (evt: { sessionId: string; fullSnapshot: boolean }) => {
      const session = sessions.get(evt.sessionId)
      if (session) {
        session.lastSeen = Date.now()
      } else {
        // Esta aba recarregou (ou abriu agora) no meio de uma sessão: adota e manda a foto inteira.
        sessions.set(evt.sessionId, { viewerName: 'Supervisor', lastSeen: Date.now() })
        refreshWatchers()
      }
      if (document.visibilityState !== 'visible') { sendState('oculta'); return }
      if (!stopRecording) startRecording()
      else if (evt.fullSnapshot || !session) record.takeFullSnapshot(true)
    }),
  ]

  document.addEventListener('visibilitychange', onVisibilityChange)
  window.addEventListener('pagehide', onPageHide)

  watchdog = setInterval(() => {
    const now = Date.now()
    for (const [id, s] of sessions) if (now - s.lastSeen > KEEPALIVE_TIMEOUT_MS) endSession(id)
  }, 5_000)
}

/** Logout: encerra tudo o que estiver sendo espelhado. */
export function stopScreenMirrorAgent(): void {
  sendState('encerrado')
  for (const id of [...sessions.keys()]) endSession(id)
  if (watchdog) clearInterval(watchdog)
  watchdog = undefined
  document.removeEventListener('visibilitychange', onVisibilityChange)
  window.removeEventListener('pagehide', onPageHide)
  unsubscribers.forEach(off => off())
  unsubscribers = []
  started = false
}
